import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, InputAdornment, Snackbar, Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Add, Delete, Payment, Edit } from '@mui/icons-material';
import dayjs from 'dayjs';
import { api } from '../api';

const PAYMENT_METHODS = ['Cash', 'Venmo', 'Zelle', 'Check', 'Bank Transfer', 'Other'];

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [form, setForm] = useState({
    student_id: '', amount: '', payment_date: dayjs(), method: '', notes: ''
  });
  const [filterStudent, setFilterStudent] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchData = async () => {
    try {
      const [paymentsData, studentsData] = await Promise.all([
        api.getPayments(filterStudent || undefined),
        api.getStudents()
      ]);
      setPayments(paymentsData);
      setStudents(studentsData);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchData(); }, [filterStudent]);

  const openDialog = (payment = null) => {
    if (payment) {
      setEditingPayment(payment);
      setForm({
        student_id: payment.student_id,
        amount: payment.amount.toString(),
        payment_date: dayjs(payment.payment_date),
        method: payment.method || '',
        notes: payment.notes || ''
      });
    } else {
      setEditingPayment(null);
      setForm({ student_id: '', amount: '', payment_date: dayjs(), method: '', notes: '' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const payload = {
      student_id: form.student_id,
      amount: parseFloat(form.amount),
      payment_date: form.payment_date.format('YYYY-MM-DD'),
      method: form.method,
      notes: form.notes
    };
    try {
      if (editingPayment) {
        await api.updatePayment(editingPayment.id, payload);
        setSnackbar({ open: true, message: 'Payment updated!', severity: 'success' });
      } else {
        await api.createPayment(payload);
        setSnackbar({ open: true, message: 'Payment recorded!', severity: 'success' });
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deletePayment(id);
      setSnackbar({ open: true, message: 'Payment deleted', severity: 'info' });
      setConfirmDelete(null);
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const totalPayments = payments.reduce((sum, p) => sum + p.amount, 0);

  return (
    <Box sx={{ p: 2, maxWidth: 700, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Payments</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => openDialog()}>
          Record Payment
        </Button>
      </Box>

      {/* Summary Card */}
      <Card sx={{ mb: 2, background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white' }}>
        <CardContent sx={{ py: 2 }}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Total Payments {filterStudent ? '(filtered)' : ''}
          </Typography>
          <Typography variant="h4" fontWeight={700}>${totalPayments.toFixed(2)}</Typography>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>{payments.length} transactions</Typography>
        </CardContent>
      </Card>

      {/* Filter */}
      <FormControl fullWidth sx={{ mb: 2 }} size="small">
        <InputLabel>Filter by Student</InputLabel>
        <Select
          value={filterStudent}
          onChange={e => setFilterStudent(e.target.value)}
          label="Filter by Student"
        >
          <MenuItem value="">All Students</MenuItem>
          {students.map(s => (
            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Payments Table */}
      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Method</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Payment sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary">No payments recorded yet</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payments.map(payment => (
                  <TableRow key={payment.id} hover>
                    <TableCell>{payment.payment_date}</TableCell>
                    <TableCell>{payment.student_name}</TableCell>
                    <TableCell>
                      {payment.method && <Chip label={payment.method} size="small" variant="outlined" />}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
                      ${payment.amount.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openDialog(payment)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => setConfirmDelete(payment.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingPayment ? 'Edit Payment' : 'Record Payment'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Student</InputLabel>
            <Select
              value={form.student_id}
              onChange={e => setForm({ ...form, student_id: e.target.value })}
              label="Student"
              disabled={!!editingPayment}
            >
              {students.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                  {s.balance_owed > 0 && ` (owes $${s.balance_owed.toFixed(2)})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth label="Amount" value={form.amount}
            onChange={e => setForm({ ...form, amount: e.target.value })}
            type="number" sx={{ mb: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          />

          <DatePicker
            label="Payment Date"
            value={form.payment_date}
            onChange={(val) => setForm({ ...form, payment_date: val })}
            sx={{ width: '100%', mb: 2 }}
            slotProps={{ textField: { fullWidth: true } }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Payment Method</InputLabel>
            <Select
              value={form.method}
              onChange={e => setForm({ ...form, method: e.target.value })}
              label="Payment Method"
            >
              {PAYMENT_METHODS.map(m => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth label="Notes (optional)" value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            multiline rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}
            disabled={!form.student_id || !form.amount}>
            {editingPayment ? 'Update' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Payment?</DialogTitle>
        <DialogContent>
          <Typography>This will remove this payment record. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(confirmDelete)}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
}
